import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLatest } from 'react-use';
import { appSearchParamsToURLSearchParams, appSearchStringToParams, } from '../appSearchParams';
/**
 * A hook that lets you work with the search params associated with an app path.
 * Reading and updating search params should in turn be done through this hook
 * as it facilitates complete type-safety.
 */
export const useAppSearchParams = (_path) => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = useMemo(() => appSearchStringToParams(location.search), [location.search]);
    const latestSearchParams = useLatest(searchParams);
    const replaceParams = useCallback((params, options = { replace: false }) => {
        const paramsString = appSearchParamsToURLSearchParams(params).toString();
        navigate(`${location.pathname}${paramsString ? `?${paramsString}` : ''}`, options);
    }, [navigate, location.pathname]);
    const patchParams = useCallback(({ set, unset }, options) => {
        replaceParams(Object.entries(Object.assign(Object.assign({}, latestSearchParams.current), set)).reduce((params, [name, value]) => unset.includes(name)
            ? params
            : Object.assign(Object.assign({}, params), { [name]: value }), {}), options);
    }, [latestSearchParams, replaceParams]);
    const setParams = useCallback((params, options) => {
        replaceParams(Object.assign(Object.assign({}, latestSearchParams.current), params), options);
    }, [latestSearchParams, replaceParams]);
    const setParam = useCallback((name, value, options) => {
        setParams({ [name]: value }, options);
    }, [setParams]);
    const unsetParams = useCallback((names, options) => {
        replaceParams(Object.entries(latestSearchParams.current).reduce((params, [name, value]) => names.includes(name)
            ? params
            : Object.assign(Object.assign({}, params), { [name]: value }), {}), options);
    }, [latestSearchParams, replaceParams]);
    const unsetParam = useCallback((name, options) => {
        unsetParams([name], options);
    }, [unsetParams]);
    return {
        paramsString: location.search,
        params: searchParams,
        setParams,
        setParam,
        unsetParams,
        unsetParam,
        patchParams,
        replaceParams,
    };
};
